import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, ShieldAlert, MapPin, Monitor, Smartphone, LogOut, KeyRound, AlertOctagon } from "lucide-react";
import { api } from "../lib/api";
import Tip from "../components/Tip";
import { toast } from "sonner";

/**
 * Sezione "Attività di accesso" — inclusa nella pagina Sicurezza.
 * Statistiche + ultimi 20 eventi + azioni sicurezza.
 */
export default function SecurityActivity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.securityActivity();
      setData(d);
    } catch (e) {
      toast.error("Impossibile caricare l'attività di accesso");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const revokeOthers = async () => {
    setBusy(true);
    try {
      await api.revokeOthers();
      toast.success("Altri dispositivi disconnessi.");
      await load();
    } catch { toast.error("Errore durante la disconnessione."); }
    finally { setBusy(false); }
  };

  const submitReport = async () => {
    if (reportText.trim().length < 5) {
      toast.error("Descrivi meglio l'attività sospetta (almeno 5 caratteri).");
      return;
    }
    setBusy(true);
    try {
      const r = await api.reportSuspicious(reportText.trim());
      toast.success(r.message || "Segnalazione inviata.");
      setReportOpen(false); setReportText("");
    } catch { toast.error("Errore durante l'invio della segnalazione."); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-sm opacity-60" data-testid="security-activity-loading">Caricamento…</div>;
  if (!data) return null;

  const stats = data.stats || {};
  const highFail = (stats.failed_last_15_min || 0) > 0;

  return (
    <div className="space-y-5" data-testid="security-activity">
      <div>
        <h3 className="font-display text-lg font-semibold" style={{ color: "var(--pxos-text-dark)" }}>Attività di accesso</h3>
        <p className="text-xs opacity-70 mt-1" style={{ color: "var(--pxos-text-dark)" }}>
          Chi è entrato (o ha provato a entrare) nel tuo account
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div
          className="rounded-xl border p-4"
          style={{ borderColor: highFail ? "#F5C24A" : "var(--pxos-border)", background: highFail ? "#FFF8E1" : "white" }}
          data-testid="stat-failed-15m"
        >
          <div className="text-[10px] uppercase tracking-widest opacity-60" style={{ color: "var(--pxos-text-dark)" }}>Ultimi 15 min</div>
          <div className="text-2xl font-display font-semibold mt-1" style={{ color: highFail ? "#8A5A00" : "var(--pxos-text-dark)" }}>
            {stats.failed_last_15_min}
          </div>
          <div className="text-xs opacity-70" style={{ color: "var(--pxos-text-dark)" }}>Tentativi falliti</div>
        </div>
        <div className="rounded-xl border p-4" style={{ borderColor: "var(--pxos-border)" }} data-testid="stat-failed-24h">
          <div className="text-[10px] uppercase tracking-widest opacity-60" style={{ color: "var(--pxos-text-dark)" }}>Ultime 24 ore</div>
          <div className="text-2xl font-display font-semibold mt-1" style={{ color: "var(--pxos-text-dark)" }}>
            {stats.failed_last_24_h}
          </div>
          <div className="text-xs opacity-70" style={{ color: "var(--pxos-text-dark)" }}>Tentativi falliti</div>
        </div>
      </div>

      {highFail && (
        <div className="rounded-xl border-2 p-4 flex items-start gap-3" style={{ borderColor: "#F5C24A", background: "#FFF8E1" }} data-testid="security-alert-banner">
          <ShieldAlert size={20} style={{ color: "#8A5A00" }} className="mt-0.5 shrink-0" />
          <div className="text-sm" style={{ color: "#8A5A00" }}>
            <strong>Attenzione:</strong> sono stati registrati tentativi di accesso falliti negli ultimi 15 minuti.
            Se non sei stata tu, cambia subito la password e attiva la 2FA.
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Tip label="Disconnette tutti gli altri dispositivi tranne questo">
          <button onClick={revokeOthers} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold bg-white border hover:bg-gray-50 disabled:opacity-50"
                  style={{ color: "var(--pxos-text-dark)", borderColor: "var(--pxos-border)" }}
                  data-testid="btn-revoke-others">
            <LogOut size={14} /> Disconnetti altri dispositivi
          </button>
        </Tip>
        <Tip label="Vai a cambia password">
          <a href="/settings/security#change-password"
             className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold bg-white border hover:bg-gray-50"
             style={{ color: "var(--pxos-text-dark)", borderColor: "var(--pxos-border)" }}
             data-testid="btn-change-password">
            <KeyRound size={14} /> Cambia password
          </a>
        </Tip>
        <Tip label="Segnala un tentativo di accesso non autorizzato">
          <button onClick={() => setReportOpen(true)} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold border hover:bg-red-50"
                  style={{ color: "#B42318", borderColor: "#F5C4C4", background: "#FFF5F5" }}
                  data-testid="btn-report-suspicious">
            <AlertOctagon size={14} /> Segnala attività sospetta
          </button>
        </Tip>
      </div>

      {/* Report modal */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="report-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setReportOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
            <h4 className="font-display text-base font-semibold" style={{ color: "var(--pxos-text-dark)" }}>Segnala attività sospetta</h4>
            <p className="text-xs mt-1 opacity-70" style={{ color: "var(--pxos-text-dark)" }}>
              Descrivi cosa è successo. Riceverai una conferma via email.
            </p>
            <textarea value={reportText} onChange={(e) => setReportText(e.target.value)}
                      rows={5} className="pxos-input mt-3 w-full text-sm"
                      placeholder="Es. Ho visto un tentativo di accesso da una città che non conosco…"
                      data-testid="report-textarea" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setReportOpen(false)}
                      className="rounded-full px-4 py-2 text-xs font-semibold border" style={{ borderColor: "var(--pxos-border)", color: "var(--pxos-text-dark)" }}
                      data-testid="report-cancel">Annulla</button>
              <button onClick={submitReport} disabled={busy}
                      className="rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
                      style={{ background: "#B42318" }}
                      data-testid="report-submit">Invia segnalazione</button>
            </div>
          </div>
        </div>
      )}

      {/* Events list */}
      <div>
        <h4 className="text-sm font-semibold mb-2" style={{ color: "var(--pxos-text-dark)" }}>Ultimi 20 accessi</h4>
        {data.events.length === 0 ? (
          <div className="text-sm opacity-60" style={{ color: "var(--pxos-text-dark)" }}>Nessun accesso registrato.</div>
        ) : (
          <ul className="divide-y rounded-xl border overflow-hidden" style={{ borderColor: "var(--pxos-border)" }} data-testid="events-list">
            {data.events.map((ev) => <EventRow key={ev.id} ev={ev} />)}
          </ul>
        )}
      </div>
    </div>
  );
}

function EventRow({ ev }) {
  const OK = ev.outcome === "success";
  const NEW = ev.outcome === "new_device";
  const d = new Date(ev.created_at);
  const label = d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
  const DeviceIcon = /iphone|android|mobile/i.test(ev.device || "") ? Smartphone : Monitor;
  return (
    <li className="flex items-center gap-3 px-3 py-3 text-sm bg-white" data-testid="event-row">
      <div className="shrink-0">
        {OK ? <CheckCircle2 size={18} className="text-emerald-600" /> :
         NEW ? <ShieldAlert size={18} style={{ color: "#8A5A00" }} /> :
              <XCircle size={18} className="text-red-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium" style={{ color: "var(--pxos-text-dark)" }}>{label}</span>
          <span
            className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
            style={{
              background: ev.recognized ? "#E7F3E9" : "#FFF3E0",
              color: ev.recognized ? "#1F7A2C" : "#8A5A00",
            }}
          >
            {ev.recognized ? "Riconosciuto" : "Non riconosciuto"}
          </span>
        </div>
        <div className="text-xs opacity-70 flex items-center gap-2 mt-0.5" style={{ color: "var(--pxos-text-dark)" }}>
          <DeviceIcon size={12} />
          <span className="truncate">{ev.device} · {ev.browser}</span>
          <MapPin size={12} className="ml-1" />
          <span>{ev.location}</span>
        </div>
      </div>
      <span className="shrink-0 text-[11px] font-semibold" style={{ color: OK ? "#1F7A2C" : NEW ? "#8A5A00" : "#B42318" }}>
        {OK ? "Riuscito" : NEW ? "Nuovo dispositivo" : "Fallito"}
      </span>
    </li>
  );
}
